import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';

// Tokens de design issus de la maquette Pencil (ecowatch-maquettes.pen / F1)
export const authColors = {
  bg: '#1C1D2E',
  bgCard: '#2E2F45',
  bgInput: '#25263B',
  bgSidebar: '#1A1B2E',
  borderSubtle: '#3A3B55',
  accentGreen: '#34D399',
  accentGreenDark: '#059669',
  accentBlue: '#22D3EE',
  danger: '#FF5722',
  success: '#22C55E',
  warning: '#F59E0B',
  textPrimary: '#FFFFFF',
  textMuted: '#A7AEC2',
  textPlaceholder: '#6D7593',
  onAccent: '#0B1320',
};

export const brandGradient =
  'linear-gradient(45deg, #0F1720 0%, #0F3B2B 55%, #054B3A 100%)';
export const btnGradient = 'linear-gradient(180deg, #34D399 0%, #10B981 100%)';
export const avatarGradient =
  'linear-gradient(135deg, #34D399 0%, #22D3EE 100%)';

export const fontStack = 'Inter, "Segoe UI", Roboto, Arial, sans-serif';

// Champ de saisie aligné sur la maquette : label au-dessus, conteneur arrondi
// avec icône à gauche et action optionnelle à droite.
export const AuthField = ({
  label,
  rightLabel,
  icon,
  endIcon,
  onEndIconClick,
  type = 'text',
  name,
  value,
  onChange,
  placeholder,
  required,
  autoComplete,
  accentBorder = false,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
    {(label || rightLabel) && (
      <Box
        sx={{
          display: 'flex',
          justifyContent: rightLabel ? 'space-between' : 'flex-start',
          alignItems: 'center',
        }}
      >
        {label && (
          <Typography
            component="label"
            sx={{
              color: authColors.textPrimary,
              fontSize: '13px',
              fontWeight: 500,
              fontFamily: fontStack,
            }}
          >
            {label}
          </Typography>
        )}
        {rightLabel}
      </Box>
    )}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        height: '48px',
        backgroundColor: authColors.bgInput,
        borderRadius: '12px',
        border: `1px solid ${accentBorder ? authColors.accentGreen : authColors.borderSubtle}`,
        px: '16px',
        transition: 'border-color 0.15s ease',
        '&:focus-within': { borderColor: authColors.accentGreen },
      }}
    >
      {icon &&
        React.cloneElement(icon, {
          sx: { fontSize: 18, color: authColors.textPlaceholder, ...(icon.props.sx || {}) },
        })}
      <Box
        component="input"
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        autoComplete={autoComplete}
        sx={{
          flex: 1,
          minWidth: 0,
          background: 'transparent',
          border: 'none',
          outline: 'none',
          color: authColors.textPrimary,
          fontFamily: fontStack,
          fontSize: '14px',
          '&::placeholder': { color: authColors.textPlaceholder },
          '&:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 1000px ${authColors.bgInput} inset !important`,
            WebkitTextFillColor: `${authColors.textPrimary} !important`,
            caretColor: authColors.textPrimary,
          },
        }}
      />
      {endIcon && (
        <IconButton
          type="button"
          onClick={onEndIconClick}
          sx={{ p: 0, color: authColors.textPlaceholder }}
        >
          {React.cloneElement(endIcon, { sx: { fontSize: 18 } })}
        </IconButton>
      )}
    </Box>
  </Box>
);

// Bouton primaire dégradé vert de la maquette.
export const AuthButton = ({ children, loading, disabled, type = 'submit', onClick, sx }) => (
  <Box
    component="button"
    type={type}
    onClick={onClick}
    disabled={loading || disabled}
    sx={{
      width: '100%',
      height: '52px',
      border: 'none',
      cursor: loading || disabled ? 'not-allowed' : 'pointer',
      borderRadius: '12px',
      background: btnGradient,
      color: authColors.onAccent,
      fontFamily: fontStack,
      fontSize: '15px',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      boxShadow: '0 10px 24px rgba(52, 211, 153, 0.33)',
      transition: 'filter 0.15s ease, opacity 0.15s ease',
      '&:hover': { filter: loading || disabled ? 'none' : 'brightness(1.05)' },
      '&:disabled': { opacity: 0.6 },
      ...sx,
    }}
  >
    {children}
  </Box>
);

// Lien texte vert (changement de vue, etc.)
export const AuthLink = ({ children, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      p: 0,
      color: authColors.accentGreen,
      fontFamily: fontStack,
      fontSize: '13px',
      fontWeight: 600,
      '&:hover': { textDecoration: 'underline' },
    }}
  >
    {children}
  </Box>
);
